import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faExternalLinkAlt, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import '@medprompt/shared/src/common/modals.css';
import './HelpButton.css';
import { getHelpContent } from './helpContent.js';

const TAB_VIDEOS = 'videos';
const TAB_DOCS = 'documentation';

const HelpModal = ({ pageId, onClose }) => {
  const content = getHelpContent(pageId);
  const videos = content?.videos || [];
  const docs = content?.documentation || [];
  const hasVideos = videos.length > 0;
  const videosComingSoon = !!content?.videosComingSoon && !hasVideos;
  const showVideosTab = hasVideos || videosComingSoon;
  const hasDocs = docs.length > 0;

  const [activeTab, setActiveTab] = useState(() =>
    showVideosTab ? TAB_VIDEOS : TAB_DOCS
  );
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);

  useEffect(() => {
    setActiveTab(showVideosTab ? TAB_VIDEOS : TAB_DOCS);
  }, [pageId, showVideosTab]);

  useEffect(() => {
    setSelectedVideoIndex(0);
  }, [pageId]);

  const videoCount = videos.length;
  const safeVideoIndex = videoCount > 0 ? Math.min(selectedVideoIndex, videoCount - 1) : 0;
  const currentVideo = videoCount > 0 ? videos[safeVideoIndex] : null;

  const goPrevVideo = useCallback(() => {
    setSelectedVideoIndex((i) => (i <= 0 ? videoCount - 1 : i - 1));
  }, [videoCount]);

  const goNextVideo = useCallback(() => {
    setSelectedVideoIndex((i) => (i >= videoCount - 1 ? 0 : i + 1));
  }, [videoCount]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  if (!content || (!showVideosTab && !hasDocs)) {
    return null;
  }

  const showTabs = showVideosTab && hasDocs;

  const modalBody = (
    <div
      className="modal-overlay help-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-container modal-animated help-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 id="help-modal-title">{content.modalTitle}</h2>
          <button type="button" className="close-button" onClick={onClose} aria-label="Close help">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {showTabs && (
          <div className="help-tab-buttons" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === TAB_VIDEOS}
              className={`help-tab ${activeTab === TAB_VIDEOS ? 'active' : ''}`}
              onClick={() => setActiveTab(TAB_VIDEOS)}
            >
              Videos
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === TAB_DOCS}
              className={`help-tab ${activeTab === TAB_DOCS ? 'active' : ''}`}
              onClick={() => setActiveTab(TAB_DOCS)}
            >
              Documentation
            </button>
          </div>
        )}

        <div className="modal-content help-modal-content">
          {(activeTab === TAB_VIDEOS || !showTabs) && videosComingSoon && (
            <div className="help-videos-panel" role="tabpanel">
              <div className="help-videos-coming-soon">
                <p>Coming soon... in the meantime please read the documentation!</p>
              </div>
            </div>
          )}

          {(activeTab === TAB_VIDEOS || !showTabs) && hasVideos && currentVideo && (
            <div className="help-videos-panel" role="tabpanel">
              <div className="help-videos-layout">
                <nav className="help-video-nav" aria-label="Tutorial videos">
                  <div className="help-video-nav-label">Tutorials</div>
                  <ul className="help-video-nav-list">
                    {videos.map((v, index) => (
                      <li key={v.id}>
                        <button
                          type="button"
                          className={`help-video-nav-item ${index === safeVideoIndex ? 'active' : ''}`}
                          onClick={() => setSelectedVideoIndex(index)}
                          aria-current={index === safeVideoIndex ? 'true' : undefined}
                        >
                          <span className="help-video-nav-title">{v.title}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>
                <div className="help-video-main">
                  <div className="help-video-main-header">
                    <h3 className="help-video-current-title">{currentVideo.title}</h3>
                    {videoCount > 1 && (
                      <div
                        className="help-video-stepper"
                        role="group"
                        aria-label={`Video ${safeVideoIndex + 1} of ${videoCount}`}
                      >
                        <button
                          type="button"
                          className="help-video-step-btn"
                          onClick={goPrevVideo}
                          aria-label="Previous video"
                        >
                          <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <button
                          type="button"
                          className="help-video-step-btn"
                          onClick={goNextVideo}
                          aria-label="Next video"
                        >
                          <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="video-embed-container">
                    <iframe
                      key={currentVideo.id}
                      title={currentVideo.title}
                      src={`https://player.vimeo.com/video/${currentVideo.id}?title=0&byline=0&portrait=0`}
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {(activeTab === TAB_DOCS || !showTabs) && hasDocs && (
            <div className="help-docs-panel" role="tabpanel">
              <ul className="documentation-links">
                {docs.map((d) => (
                  <li key={d.url}>
                    <a href={d.url} target="_blank" rel="noopener noreferrer" className="help-doc-link">
                      <span>{d.title}</span>
                      <FontAwesomeIcon icon={faExternalLinkAlt} className="help-doc-link-icon" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalBody, document.body);
};

export default HelpModal;
